import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const thisYear = new Date().getFullYear();

export default function YearManagement() {
  const [firms, setFirms] = useState(null);
  const [year, setYear] = useState(String(thisYear + 1));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/years/overview");
      setFirms(data.firms);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setFirms([]);
    }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    const y = parseInt(year, 10);
    if (!y || y < 2000 || y > 2100) { toast.error("Geçerli bir yıl girin (2000-2100)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/years/start", { year: y });
      const s = data.started?.length || 0;
      const a = data.already?.length || 0;
      toast.success(`${y} yılı başlatıldı. ${s} firmada eklendi${a ? `, ${a} firmada zaten vardı` : ""}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="year-management-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Yıl Yönetimi</h1>
        <p className="text-sm text-slate-500 mt-1">
          Yeni yıl başlatmak yalnızca yılı seçilebilir hâle getirir. Önceki yılların dosya, ziyaret,
          eğitim ve kayıtları korunur ve yıl seçilerek erişilir. Hiçbir evrak çoğaltılmaz veya tarihi değişmez.
        </p>
      </div>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-display text-base">Yeni Yıl Başlat</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Yıl</Label>
            <Input type="number" className="w-32" value={year} min={2000} max={2100}
              onChange={(e) => setYear(e.target.value)} data-testid="year-start-input" />
          </div>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={start} disabled={busy} data-testid="year-start-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarPlus className="h-4 w-4 mr-2" />}
            Tüm Firmalarım İçin Başlat
          </Button>
          <p className="text-xs text-slate-400 w-full">
            Ana yönetici yetkili olduğu tüm firmalar için; danışman yalnızca atandığı firmalar için başlatır.
            Aynı yıl ikinci kez başlatılırsa mükerrer kayıt oluşmaz.
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-display text-base">Firmaların Başlatılmış Yılları</CardTitle></CardHeader>
        <CardContent>
          {firms === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>
          ) : firms.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">Erişilebilir firma yok.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {firms.map((f) => (
                <div key={f.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`year-firm-${f.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-slate-800">{f.title}</span>
                    <StatusBadge tone={f.is_active ? "active" : "passive"}>{f.is_active ? "Aktif" : "Pasif"}</StatusBadge>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(f.started_years || []).length === 0 ? (
                      <span className="text-xs text-slate-400">Başlatılmış yıl yok (varsayılan yıllar gösterilir)</span>
                    ) : f.started_years.map((y) => (
                      <span key={y} className="inline-flex items-center rounded-md bg-sky-50 border border-sky-200 px-2 py-0.5 text-xs font-semibold text-sky-700">{y}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
